import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import {
  MarketplaceListing,
  ListingBuyer,
} from '@/types/residents/marketplaceListing';
import { MARKETPLACE_API, CACHE_TAGS } from '@/constants/residents/marketplaceConstants';
import {
  ApiResponse,
  ListingsQueryParams,
  CreateListingRequest,
  UpdateListingRequest,
  buildListingFormData,
} from './types';

// ============================================================================
// Listings API
// ============================================================================

export const listingsApi = createApi({
  reducerPath: 'marketplaceListingsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    CACHE_TAGS.Listing,
    CACHE_TAGS.MyListings,
    CACHE_TAGS.Profile,
  ],
  endpoints: (builder) => ({
    /**
     * Get all active listings
     */
    getListings: builder.query<MarketplaceListing[], ListingsQueryParams>({
      query: (params) => ({
        url: MARKETPLACE_API.listings.base,
        method: 'GET',
        params,
      }),
      transformResponse: (response: ApiResponse<MarketplaceListing[]> | null) => {
        if (!response || !response.data) {
          console.warn('getListings: Empty or null response from API');
          return [];
        }
        return response.data;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Listing' as const, id })),
              { type: CACHE_TAGS.Listing, id: 'LIST' },
            ]
          : [{ type: CACHE_TAGS.Listing, id: 'LIST' }],
    }),

    /**
     * Get a single listing by ID
     */
    getListing: builder.query<MarketplaceListing | null, string>({
      query: (id) => ({
        url: MARKETPLACE_API.listings.byId(id),
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<MarketplaceListing> | null) => {
        if (!response || !response.data) {
          console.warn('getListing: Empty or null response from API');
          return null;
        }
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: CACHE_TAGS.Listing, id }],
    }),

    /**
     * Create a new listing
     */
    createListing: builder.mutation<{ id: string; warning?: string } | null, CreateListingRequest>({
      query: (data) => ({
        url: MARKETPLACE_API.listings.base,
        method: 'POST',
        body: buildListingFormData(data),
        formData: true,
      }),
      transformResponse: (response: ApiResponse<{ id: string }> | null) => {
        if (!response || !response.data) {
          console.warn('createListing: Empty or null response from API');
          return null;
        }
        return {
          id: response.data.id,
          warning: response.warning,
        };
      },
      invalidatesTags: [
        { type: CACHE_TAGS.Listing, id: 'LIST' },
        { type: CACHE_TAGS.MyListings, id: 'LIST' },
        { type: CACHE_TAGS.Profile, id: 'ME' },
      ],
    }),

    /**
     * Update an existing listing
     */
    updateListing: builder.mutation<{ id: string; warning?: string } | null, UpdateListingRequest>({
      query: ({ id, ...data }) => ({
        url: MARKETPLACE_API.listings.byId(id),
        method: 'PATCH',
        body: buildListingFormData(data),
        formData: true,
      }),
      transformResponse: (response: ApiResponse<{ id: string }> | null) => {
        if (!response || !response.data) {
          console.warn('updateListing: Empty or null response from API');
          return null;
        }
        return {
          id: response.data.id,
          warning: response.warning,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: CACHE_TAGS.Listing, id },
        { type: CACHE_TAGS.Listing, id: 'LIST' },
        { type: CACHE_TAGS.MyListings, id: 'LIST' },
      ],
    }),

    /**
     * Delete a listing
     */
    deleteListing: builder.mutation<void, string>({
      query: (id) => ({
        url: MARKETPLACE_API.listings.byId(id),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: CACHE_TAGS.Listing, id },
        { type: CACHE_TAGS.Listing, id: 'LIST' },
        { type: CACHE_TAGS.MyListings, id: 'LIST' },
        { type: CACHE_TAGS.Profile, id: 'ME' },
      ],
    }),

    /**
     * Mark a listing as sold
     */
    markAsSold: builder.mutation<void, string>({
      query: (id) => ({
        url: MARKETPLACE_API.listings.sold(id),
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: CACHE_TAGS.Listing, id },
        { type: CACHE_TAGS.Listing, id: 'LIST' },
        { type: CACHE_TAGS.MyListings, id: 'LIST' },
        { type: CACHE_TAGS.Profile, id: 'ME' },
      ],
    }),

    /**
     * Repost a sold listing
     */
    repostListing: builder.mutation<void, string>({
      query: (id) => ({
        url: MARKETPLACE_API.listings.repost(id),
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: CACHE_TAGS.Listing, id },
        { type: CACHE_TAGS.Listing, id: 'LIST' },
        { type: CACHE_TAGS.MyListings, id: 'LIST' },
      ],
    }),

    /**
     * Get buyers who have messaged about a listing (for sold feedback flow)
     */
    getListingBuyers: builder.query<ListingBuyer[], string>({
      query: (id) => ({
        url: MARKETPLACE_API.listings.buyers(id),
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<ListingBuyer[]> | null): ListingBuyer[] => {
        if (!response || !response.data) {
          return [];
        }
        return response.data;
      },
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetListingsQuery,
  useLazyGetListingsQuery,
  useGetListingQuery,
  useLazyGetListingQuery,
  useCreateListingMutation,
  useUpdateListingMutation,
  useDeleteListingMutation,
  useMarkAsSoldMutation,
  useRepostListingMutation,
  useGetListingBuyersQuery,
  useLazyGetListingBuyersQuery,
} = listingsApi;

